import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Handshake, DollarSign, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsignmentWidget() {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/consignment`, { headers: getAuthHeader() });
        if (!alive) return;
        setStats(r.data?.stats || null);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    };
    load();
    const id = setInterval(load, 120_000);
    return () => { alive = false; clearInterval(id); };
  }, [getAuthHeader]);

  const submitted = stats?.by_status?.submitted || 0;
  const needsReview = stats?.by_status?.needs_review || 0;
  const awaitingShipment = stats?.by_status?.awaiting_shipment || 0;
  const received = stats?.by_status?.received || 0;
  const listed = stats?.by_status?.listed || 0;
  const sold = stats?.by_status?.sold || 0;
  const payoutsPending = stats?.payouts_pending || 0;
  const liability = stats?.total_liability || 0;

  // Anything that needs the admin's attention right now
  const actionable = submitted + needsReview + received;

  return (
    <Link to="/admin/consignment" className="block border border-zinc-800 hover:border-tiffany-500/40 transition-colors p-5 bg-black" data-testid="consignment-widget">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-mono text-sm text-zinc-400 flex items-center gap-2">
          <Handshake className="w-4 h-4" />
          CONSIGNMENT
        </h3>
        <span className="font-mono text-[10px] text-zinc-600 tracking-wider">VIEW →</span>
      </div>

      {loading ? (
        <p className="font-mono text-xs text-zinc-600">LOADING_</p>
      ) : !stats ? (
        <p className="font-mono text-xs text-zinc-500">NO DATA</p>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="text-center" title="Needs review (submitted + needs_review + received)">
              <p className={`font-mono text-base font-bold ${actionable > 0 ? 'text-yellow-400' : 'text-zinc-500'}`} data-testid="widget-cons-actionable">{actionable}</p>
              <p className="font-mono text-[9px] text-zinc-600 tracking-wider">ACTION</p>
            </div>
            <div className="text-center" title="Awaiting seller shipment">
              <p className={`font-mono text-base font-bold ${awaitingShipment > 0 ? 'text-blue-400' : 'text-zinc-500'}`} data-testid="widget-cons-await">{awaitingShipment}</p>
              <p className="font-mono text-[9px] text-zinc-600 tracking-wider">SHIPPING</p>
            </div>
            <div className="text-center">
              <p className="font-mono text-base font-bold text-tiffany-500" data-testid="widget-cons-listed">{listed}</p>
              <p className="font-mono text-[9px] text-zinc-600 tracking-wider">LISTED</p>
            </div>
            <div className="text-center">
              <p className="font-mono text-base font-bold text-purple-400" data-testid="widget-cons-sold">{sold}</p>
              <p className="font-mono text-[9px] text-zinc-600 tracking-wider">SOLD</p>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-3 flex items-center justify-between">
            <p className="font-mono text-[10px] text-zinc-500 tracking-widest flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              LIABILITY <span className="text-tiffany-500">${liability.toLocaleString()}</span>
            </p>
            {payoutsPending > 0 && (
              <p className="font-mono text-[10px] text-yellow-400 tracking-widest flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {payoutsPending} PAYOUTS
              </p>
            )}
          </div>
        </>
      )}
    </Link>
  );
}
